import AddProductForm from '@components/provisioning/custom-provision/AddProductForm';
import AllocationInstructionsEmailForm from './AllocationInstructionsEmailForm';
import CloseIcon from '@mui/icons-material/Close';
import ModifyQuotaForm from './ModifyQuotaForm';
import Notifier from '@components/common/Notifier';
import PropTypes from 'prop-types';
import ProvisioningDetailsStep from './ProvisioningDetailsStep';
import React, {useCallback, useEffect, useState} from 'react';
import useAddLineItems from '@hooks/data/planetary-variables/mutations/useAddLineItems';
import {
  Alert,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import {formatErrorMsg} from '@account/utils/errors';
import {normalizeProductVariants} from '@hooks/data/products/useProductVariantSelectors';
import {useProductVariants} from '@hooks/data/products/useProductVariants';
import {useQueryClient} from '@tanstack/react-query';

const STEP = {
  DETAILS: 'details',
  ADD_PRODUCT: 'add-product',
  MODIFY_QUOTA: 'modify-quota',
  EMAIL: 'email',
};

const stepTitle = {
  [STEP.DETAILS]: 'Provisioning details',
  [STEP.ADD_PRODUCT]: null, // AddProductForm renders its own title
  [STEP.MODIFY_QUOTA]: 'Adjust quota',
  [STEP.EMAIL]: 'Send allocation instructions',
};

const AddProductToAccountModal = ({
  open,
  onClose,
  customerAccountId,
  isEmergencyOverride = false,
  mode,
  productAccess,
}) => {
  const queryClient = useQueryClient();
  const {data: productVariantsData} = useProductVariants();
  const [step, setStep] = useState(STEP.DETAILS);
  const [contractId, setContractId] = useState(null);
  const [isCreatingContract, setIsCreatingContract] = useState(false);
  const [isAdjustingQuota, setIsAdjustingQuota] = useState(false);
  const [lastAddedProduct, setLastAddedProduct] = useState(null);
  const [hasAddedProduct, setHasAddedProduct] = useState(false);
  const [addFormKey, setAddFormKey] = useState(0);
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const resetState = useCallback(() => {
    setStep(STEP.DETAILS);
    setContractId(null);
    setIsCreatingContract(false);
    setIsAdjustingQuota(false);
    setLastAddedProduct(null);
    setHasAddedProduct(false);
    setAddFormKey(0);
    setSuccessMessage(null);
    setErrorMessage(null);
  }, []);

  useEffect(() => {
    if (open) {
      resetState();
    }
  }, [open, resetState]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  // "Done" in the add-product step (footer button once ≥1 product is added)
  // offers to email the customer their allocation instructions rather than
  // closing outright. Cancel (shown before the first add), the X, and the
  // backdrop still close directly — the CA-page button covers a later send.
  const handleAddProductClose = useCallback(() => {
    if (hasAddedProduct) {
      setStep(STEP.EMAIL);
    } else {
      handleClose();
    }
  }, [hasAddedProduct, handleClose]);

  const handleDetailsSuccess = useCallback(
    id => {
      setIsCreatingContract(false);
      setContractId(id);
      setStep(mode === 'modify-quota' ? STEP.MODIFY_QUOTA : STEP.ADD_PRODUCT);
    },
    [mode]
  );

  const pendingProductRef = React.useRef(null);

  // Used by AddProductForm (add-product mode)
  const {mutate: addLineItems, isLoading: isAddingProduct} = useAddLineItems({
    onSuccess: () => {
      queryClient.invalidateQueries(['productAccesses']);
      queryClient.invalidateQueries(['entitlements']);
      setLastAddedProduct(pendingProductRef.current);
      setHasAddedProduct(true);
      setAddFormKey(k => k + 1);
    },
    onError: error => setErrorMessage(formatErrorMsg(error)),
  });

  const handleAddProduct = useCallback(
    ({lineItems, productVariantId: pvId}) => {
      const variants = normalizeProductVariants(productVariantsData);
      pendingProductRef.current = variants?.[pvId]?.title || pvId;
      addLineItems(lineItems);
    },
    [addLineItems, productVariantsData]
  );

  // Used by ModifyQuotaForm (modify-quota mode)
  const handleQuotaApplied = useCallback(() => {
    queryClient.invalidateQueries(['productAccesses']);
    queryClient.invalidateQueries(['entitlements']);
    setSuccessMessage('Quota updated successfully');
    handleClose();
  }, [queryClient, handleClose]);

  const title = stepTitle[step];

  return (
    <>
      <Dialog
        disableEscapeKeyDown
        open={open}
        onClose={(_, reason) => {
          if (reason === 'backdropClick') return;
          handleClose();
        }}
        maxWidth="sm"
        fullWidth
      >
        {title && (
          <DialogTitle>
            <Typography variant="h6">{title}</Typography>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{position: 'absolute', right: 8, top: 8}}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
        )}

        {step === STEP.DETAILS && (
          <DialogContent>
            <ProvisioningDetailsStep
              customerAccountId={customerAccountId}
              isEmergencyOverride={isEmergencyOverride}
              onError={error => setErrorMessage(formatErrorMsg(error))}
              onLoadingChange={setIsCreatingContract}
              onSuccess={handleDetailsSuccess}
            />
          </DialogContent>
        )}

        {step === STEP.ADD_PRODUCT && contractId && (
          <>
            {lastAddedProduct && (
              <Alert
                severity="success"
                onClose={() => setLastAddedProduct(null)}
                sx={{mx: 3, mt: 2}}
              >
                Added {lastAddedProduct}
              </Alert>
            )}
            <AddProductForm
              key={addFormKey}
              contractId={contractId}
              onAddProduct={handleAddProduct}
              onClose={handleAddProductClose}
              keepOpenOnSuccess
              hasAddedProduct={hasAddedProduct}
            />
          </>
        )}

        {step === STEP.EMAIL && (
          <DialogContent>
            <AllocationInstructionsEmailForm
              customerAccountId={customerAccountId}
              onDone={handleClose}
            />
          </DialogContent>
        )}

        {step === STEP.MODIFY_QUOTA && contractId && productAccess && (
          <DialogContent>
            <ModifyQuotaForm
              contractId={contractId}
              productAccess={productAccess}
              onAddProduct={handleQuotaApplied}
              onClose={handleClose}
              onError={error => setErrorMessage(formatErrorMsg(error))}
              onLoadingChange={setIsAdjustingQuota}
            />
          </DialogContent>
        )}

        {step === STEP.MODIFY_QUOTA && contractId && !productAccess && (
          <DialogContent>
            <Alert severity="error">
              Product data is unavailable, so quota can&apos;t be adjusted.
              Close this dialog and try again.
            </Alert>
          </DialogContent>
        )}
      </Dialog>

      <Notifier
        open={isCreatingContract}
        message="Creating contract…"
        progress
      />

      <Notifier open={isAdjustingQuota} message="Adjusting quota…" progress />
      <Notifier open={isAddingProduct} message="Adding product…" progress />

      <Notifier
        open={!!successMessage}
        message={successMessage}
        severity="success"
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
      />

      <Notifier
        open={!!errorMessage}
        message={errorMessage}
        severity="error"
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
      />
    </>
  );
};

AddProductToAccountModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  customerAccountId: PropTypes.string.isRequired,
  isEmergencyOverride: PropTypes.bool,
  mode: PropTypes.oneOf(['add-product', 'modify-quota']).isRequired,
  productAccess: PropTypes.object,
};

export default AddProductToAccountModal;
